/**
 * Ce provider centralise les données métier liées à l'utilisateur connecté :
 * - ses clients accessibles
 * - ses informations métier
 * - les tags accessibles pour le client courant
 * - les permissions dérivées (ex: admin)
 *
 * L'objectif est de fournir un état "business" stable au reste de l'application,
 * afin d'éviter les rendus intermédiaires incohérents pendant l'initialisation.
 */

import { skipToken } from '@reduxjs/toolkit/query'
import { baseApi } from 'core/api/base'
import { useGetMyClientsQuery, useGetMyUserQuery } from 'core/api/me'
import { useGetMyTagsByClientQuery } from 'core/api/tag'
import type { IApiClientTagMe } from 'core/interfaces/Me'
import { IClient } from 'core/interfaces/User'
import { IUtilisateur } from 'core/interfaces/Utilisateur'
import { UseKeycloakService } from 'core/packages/KeycloakProvider'
import { clearError } from 'core/store/slices/error'
import { tagIdsWithPermission } from 'core/utils/tagMePermissions'
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'

type IUtilisateurSession = IUtilisateur & {
  /** Client courant sélectionné/résolu côté front. */
  idClient: number
}

interface IUserContextType {
  clients: IClient[]
  isAdmin: boolean
  /** Tags du client courant pour l'utilisateur connecté (GET /clients/{id}/tags/me). */
  tagsMe: IApiClientTagMe[]
  businessStatus: 'idle' | 'loading' | 'ready' | 'error'
  businessError: unknown | null
  user: IUtilisateurSession | null
  changeClient: (clientId: number) => void
  clearUserSession: () => void
}

export const UserContext = createContext<IUserContextType>({
  clients: [],
  isAdmin: false,
  tagsMe: [],
  businessStatus: 'idle',
  businessError: null,
  user: null,
  changeClient: () => {},
  clearUserSession: () => {},
})

const saveIdClientToStorage = (idClient: number | undefined) => {
  if (typeof idClient !== 'number') return
  localStorage.setItem('idClient', idClient.toString())
}

const clearIdClientFromStorage = () => {
  localStorage.removeItem('idClient')
}

const readIdClientFromStorage = (): number | null => {
  const raw = localStorage.getItem('idClient')
  if (!raw) return null

  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Résout le client courant de manière déterministe :
 * 1. conserver le précédent s'il existe encore
 * 2. reprendre celui stocké en localStorage s'il est encore valide
 * 3. privilégier L2D si présent
 * 4. sinon prendre le premier client disponible
 */
const resolveSelectedClientId = (
  availableClients: IClient[],
  previousClientId: number | null
): number | null => {
  if (availableClients.length === 0) {
    return null
  }

  if (
    previousClientId !== null &&
    availableClients.some((client) => client.id === previousClientId)
  ) {
    return previousClientId
  }

  const storedClientId = readIdClientFromStorage()
  if (
    storedClientId !== null &&
    availableClients.some((client) => client.id === storedClientId)
  ) {
    return storedClientId
  }

  const l2dClient = availableClients.find(
    (client) => client.nomClient === 'L2D'
  )
  if (l2dClient) {
    return l2dClient.id
  }

  return availableClients[0]?.id ?? null
}

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const kc = UseKeycloakService()
  const dispatch = useDispatch()

  /**
   * État minimal local :
   * - selectedClientId : piloté localement car il dépend d'une résolution métier
   *
   * On évite de stocker dans le state ce qui peut être dérivé des queries.
   */
  const [selectedClientId, setSelectedClientId] = useState<number | null>(
    readIdClientFromStorage()
  )

  const isAuthReady = kc.isInitialized() && kc.isAutenticated()

  const myClientsQuery = useGetMyClientsQuery(undefined, {
    skip: !isAuthReady,
  })

  const myUserQuery = useGetMyUserQuery(undefined, {
    skip: !isAuthReady,
  })

  /**
   * Clients métier dérivés de l'API.
   */
  const clients = useMemo<IClient[]>(() => {
    const data = myClientsQuery.data ?? []

    return data.map((client) => ({
      id: client.idClient,
      nomClient: client.nomClient,
    }))
  }, [myClientsQuery.data])

  /**
   * Client courant effectivement utilisé dans l'application.
   * On le dérive depuis :
   * - le state local actuel
   * - les clients réellement disponibles
   */
  const resolvedSelectedClientId = useMemo(() => {
    if (!isAuthReady) {
      return null
    }

    if (!myClientsQuery.data) {
      return null
    }

    return resolveSelectedClientId(clients, selectedClientId)
  }, [isAuthReady, myClientsQuery.data, clients, selectedClientId])

  const tagsQueryArg =
    resolvedSelectedClientId !== null
      ? { idClient: resolvedSelectedClientId }
      : skipToken

  const myTagsQuery = useGetMyTagsByClientQuery(tagsQueryArg)

  const tagsMe = useMemo(() => myTagsQuery.data ?? [], [myTagsQuery.data])

  const myAdministrateurTagIds = useMemo(
    () => tagIdsWithPermission(tagsMe, 'administrateur'),
    [tagsMe]
  )

  const isAdmin = useMemo(
    () => myAdministrateurTagIds.length > 0,
    [myAdministrateurTagIds]
  )

  /**
   * Données utilisateur métier dérivées de l'API.
   * Le client courant (idClient) est injecté depuis la résolution locale.
   */
  const computedUser = useMemo<IUtilisateurSession | null>(() => {
    if (!isAuthReady) {
      return null
    }

    const businessUser = myUserQuery.data
    const idClient = resolvedSelectedClientId

    if (!businessUser || idClient === null) {
      return null
    }

    return {
      idUtilisateur: businessUser.idUtilisateur,
      idKeycloak: businessUser.idKeycloak,
      nomUtilisateur: businessUser.nomUtilisateur,
      prenom: businessUser.prenom,
      nom: businessUser.nom,
      email: businessUser.email,
      compteService: businessUser.compteService,
      superAdmin: businessUser.superAdmin,
      idClient,
    }
  }, [isAuthReady, myUserQuery.data, resolvedSelectedClientId])

  /**
   * État business dérivé.
   *
   * On distingue :
   * - idle    : auth pas encore prête
   * - loading : auth prête mais bootstrap métier incomplet
   * - error   : une des requêtes a échoué
   * - ready   : tout ce qu'il faut au layout / aux permissions est stable
   */
  const businessStatus = useMemo<'idle' | 'loading' | 'ready' | 'error'>(() => {
    if (!isAuthReady) {
      return 'idle'
    }

    if (myClientsQuery.isError || myUserQuery.isError || myTagsQuery.isError) {
      return 'error'
    }

    /**
     * Tant que les clients ne sont pas chargés, on ne peut pas résoudre le client courant.
     */
    if (!myClientsQuery.data || myClientsQuery.isLoading) {
      return 'loading'
    }

    /**
     * Tant que les infos utilisateur métier ne sont pas chargées, on attend.
     */
    if (!myUserQuery.data || myUserQuery.isLoading) {
      return 'loading'
    }

    /**
     * Si aucun client n'est disponible, on considère l'état prêt.
     * À adapter si ton métier impose au moins un client.
     */
    if (clients.length === 0) {
      return 'ready'
    }

    /**
     * Tant que le client courant n'est pas résolu, on attend.
     */
    if (resolvedSelectedClientId === null) {
      return 'loading'
    }

    /**
     * Les tags sont requis pour stabiliser les permissions dépendantes du client courant.
     */
    if (!myTagsQuery.data || myTagsQuery.isLoading) {
      return 'loading'
    }

    return 'ready'
  }, [
    isAuthReady,
    myClientsQuery.isError,
    myClientsQuery.data,
    myClientsQuery.isLoading,
    myUserQuery.isError,
    myUserQuery.data,
    myUserQuery.isLoading,
    myTagsQuery.isError,
    myTagsQuery.data,
    myTagsQuery.isLoading,
    clients.length,
    resolvedSelectedClientId,
  ])

  const businessError = useMemo<unknown | null>(() => {
    if (myClientsQuery.isError) {
      return myClientsQuery.error
    }

    if (myUserQuery.isError) {
      return myUserQuery.error
    }

    if (myTagsQuery.isError) {
      return myTagsQuery.error
    }

    return null
  }, [
    myClientsQuery.isError,
    myClientsQuery.error,
    myUserQuery.isError,
    myUserQuery.error,
    myTagsQuery.isError,
    myTagsQuery.error,
  ])

  /**
   * Nettoie complètement la session côté front :
   * - état local du client courant
   * - localStorage
   * - état d'erreur Redux (sinon une 403 persiste après re-login)
   * - cache RTK Query (sinon les queries restent en error et l'effet de
   *   résolution sur selectedClientId pourrait relancer une requête juste
   *   avant la redirection Keycloak)
   */
  const clearUserSession = useCallback(() => {
    setSelectedClientId(null)
    clearIdClientFromStorage()
    dispatch(clearError())
    dispatch(baseApi.util.resetApiState())
  }, [dispatch])

  const changeClient = useCallback((clientId: number) => {
    setSelectedClientId(clientId)
    saveIdClientToStorage(clientId)
  }, [])

  useEffect(() => {
    if (resolvedSelectedClientId === null) {
      return
    }

    if (resolvedSelectedClientId === selectedClientId) {
      return
    }

    setSelectedClientId(resolvedSelectedClientId)
    saveIdClientToStorage(resolvedSelectedClientId)
  }, [resolvedSelectedClientId, selectedClientId])

  const value = useMemo<IUserContextType>(
    () => ({
      clients,
      isAdmin,
      tagsMe,
      businessStatus,
      businessError,
      user: computedUser,
      changeClient,
      clearUserSession,
    }),
    [
      clients,
      isAdmin,
      tagsMe,
      businessStatus,
      businessError,
      computedUser,
      changeClient,
      clearUserSession,
    ]
  )

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}
